import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSession } from "../app/SessionContext";
import { defaultLayout, normalizeLayout, PREFERENCE_KEY, type NavLayout } from "./navigation";

export type LayoutStore = "platform" | "browser";

interface NavLayoutValue {
  layout: NavLayout;
  /** Where the layout is kept: on the platform for this person, or — when the platform will not — this browser. */
  store: LayoutStore;
  save: (layout: NavLayout) => Promise<void>;
  reset: () => Promise<void>;
}

const Ctx = createContext<NavLayoutValue | null>(null);

const cacheKey = (who: string) => `sky.nav.layout.${who}`;

function readCache(who: string): NavLayout | null {
  try {
    const raw = localStorage.getItem(cacheKey(who));
    return raw ? normalizeLayout(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCache(who: string, layout: NavLayout | null) {
  try {
    if (layout) localStorage.setItem(cacheKey(who), JSON.stringify(layout));
    else localStorage.removeItem(cacheKey(who));
  } catch {
    /* storage unavailable: the platform copy is the one that counts */
  }
}

/**
 * The person's menu layout.
 *
 * Drawn at once from this browser's copy, so the menu does not jump on every
 * load, then confirmed against the platform, which is the copy that follows
 * the person to another device. If the platform will not keep it — the sample
 * source, or a sign-in that names no person — the layout is kept in this
 * browser and the customise dialog says so, rather than appearing to save.
 */
export function NavLayoutProvider({ children }: { children: ReactNode }) {
  const { source, principal, tenant } = useSession();
  const who = `${tenant ?? "tenant"}.${principal.subject ?? principal.name}`;
  const [layout, setLayout] = useState<NavLayout>(() => readCache(who) ?? defaultLayout());
  const [store, setStore] = useState<LayoutStore>(source.kind === "platform" ? "platform" : "browser");

  useEffect(() => {
    let live = true;
    source
      .getPreference(PREFERENCE_KEY)
      .then((value) => {
        if (!live) return;
        const next = value == null ? defaultLayout() : normalizeLayout(value);
        setLayout(next);
        writeCache(who, value == null ? null : next);
        setStore(source.kind === "platform" ? "platform" : "browser");
      })
      .catch(() => {
        if (live) setStore("browser");
      });
    return () => {
      live = false;
    };
  }, [source, who]);

  const save = useCallback(
    async (next: NavLayout) => {
      const clean = normalizeLayout(next);
      setLayout(clean);
      writeCache(who, clean);
      try {
        await source.setPreference(PREFERENCE_KEY, clean);
        setStore(source.kind === "platform" ? "platform" : "browser");
      } catch (e) {
        setStore("browser");
        throw e;
      }
    },
    [source, who],
  );

  const reset = useCallback(async () => {
    setLayout(defaultLayout());
    writeCache(who, null);
    await source.deletePreference(PREFERENCE_KEY).catch(() => setStore("browser"));
  }, [source, who]);

  const value = useMemo(() => ({ layout, store, save, reset }), [layout, store, save, reset]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNavLayout(): NavLayoutValue {
  const v = useContext(Ctx);
  // Outside the shell (a test rendering one screen) the default menu applies.
  return (
    v ?? {
      layout: defaultLayout(),
      store: "browser",
      save: async () => undefined,
      reset: async () => undefined,
    }
  );
}
